import React, { Fragment, useContext, useMemo, useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Maximize, User, LogOut, Bell, AlignCenter } from "react-feather";
import { FiMessageSquare } from "react-icons/fi";
import CustomContext from "../../../_helper/Customizer";
import { AuthContext } from "../../../Auth/AuthContext";
import AxiosInstance from "../../../services/AxiosInstance";
import logo from "../../../assets/images/logo/logo.png";

const Header = () => {
    const navigate = useNavigate();
    const { toggleIcon, toggleSidebar } = useContext(CustomContext);
    const { logout } = useContext(AuthContext);
    const [notificationCount, setNotificationCount] = useState(0);
    const [chatThreadsUnread, setChatThreadsUnread] = useState(0);

    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user_data") || "{}");
        } catch {
            return {};
        }
    }, []);

    const getRoleLabelFromUser = (u) => {
        const directRole =
            u?.user_role_title ||
            u?.role_label ||
            u?.role_name ||
            u?.user_role ||
            u?.role;

        if (String(directRole || "").trim()) return String(directRole).trim();
        if (u?.is_super === 1 || u?.is_super === "1") return "Super Admin";
        if (String(u?.type || "").trim()) {
            const t = String(u.type).trim();
            return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
        }
        return "Admin";
    };

    const displayName = userData.name || userData.email || "Administrator";
    const [roleLabel, setRoleLabel] = useState(() => getRoleLabelFromUser(userData));
    const avatarName = (displayName || "A").trim().split(/\s+/).map((s) => s[0]).join("").toUpperCase().slice(0, 2) || "AD";

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    };

    const handleLogout = (e) => {
        e.preventDefault();
        // Clear local storage immediately
        localStorage.removeItem("user_data");
        localStorage.removeItem("_token");
        localStorage.removeItem("jobseeker_resume_redirected");
        localStorage.removeItem("jobseeker_education_redirected");
        logout();
        // Redirect after clearing storage
        setTimeout(() => {
            window.location.href = "/job/admin/login";
        }, 500);
    };

    useEffect(() => {
        let cancelled = false;
        const fetchNotificationCount = async () => {
            try {
                const res = await AxiosInstance().post("/notifications?page=1&per_page=1", { is_read: 0 });
                if (!cancelled && res.data?.count != null) setNotificationCount(Number(res.data.count));
            } catch {
                if (!cancelled) setNotificationCount(0);
            }
        };
        fetchNotificationCount();
        const interval = setInterval(fetchNotificationCount, 60000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadChatUnread = async () => {
            try {
                const res = await AxiosInstance().post("/get_chat_members");
                if (!cancelled && res.data?.total_unread != null) {
                    setChatThreadsUnread(Number(res.data.total_unread));
                }
            } catch {
                if (!cancelled) setChatThreadsUnread(0);
            }
        };
        loadChatUnread();
        const interval = setInterval(loadChatUnread, 120000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const syncRoleLabel = async () => {
            setRoleLabel(getRoleLabelFromUser(userData));

            if (userData?.is_super === 1 || userData?.is_super === "1") return;
            if (!userData?.user_role_id) return;

            try {
                const res = await AxiosInstance().post("/admin/role/get_roles", {});
                if (cancelled) return;
                const roles = Array.isArray(res.data?.data) ? res.data.data : [];
                const matchedRole = roles.find((r) => String(r.user_role_id) === String(userData.user_role_id));
                const matchedLabel = matchedRole?.user_role_title || matchedRole?.role_name;
                if (String(matchedLabel || "").trim()) {
                    setRoleLabel(String(matchedLabel).trim());
                }
            } catch {
                // Keep fallback role label from local user data.
            }
        };

        syncRoleLabel();
        return () => {
            cancelled = true;
        };
    }, [userData]);

    return (
        <Fragment>
            <div className={`page-header ${toggleIcon ? "close_icon" : ""}`}>
                <Row className="header-wrapper m-0 align-items-center">
                    <Col className="header-logo-wrapper col-auto p-0" id="out_side_click">
                        <div className="logo-wrapper">
                            <Link to="/job/admin/dashboard">
                                <img className="img-fluid for-light" src={logo} alt="Admin" style={{ width: "120px" }} />
                            </Link>
                        </div>
                        <div
                            className="toggle-sidebar"
                            onClick={() => toggleSidebar(!toggleIcon)}
                            style={{ cursor: "pointer" }}
                        >
                            <AlignCenter className="status_toggle middle sidebar-toggle" id="sidebar-toggle" />
                        </div>
                    </Col>

                    <Col xxl={5} xl={6} lg={5} md={4} sm={3} className="left-header p-0">
                        <h5 className="mb-0 fw-bold d-none d-md-block">Admin Resource Center</h5>
                    </Col>

                    <Col xxl={7} xl={6} md={7} className="nav-right pull-right right-header col-8 p-0 ms-auto">
                        <ul className="simple-list nav-menus flex-row mb-0">
                            <li className="onhover-dropdown pe-2">
                                <div
                                    className={`notification-box position-relative d-inline-flex align-items-center justify-content-center ${chatThreadsUnread > 0 ? "animated" : ""}`}
                                    style={{ cursor: "pointer", minWidth: 28 }}
                                    onClick={() => navigate("/job/admin/chat")}
                                    onKeyDown={(e) => e.key === "Enter" && navigate("/job/admin/chat")}
                                    role="button"
                                    tabIndex={0}
                                    title="Jobseeker & recruiter chats"
                                >
                                    <FiMessageSquare size={22} />
                                    {chatThreadsUnread > 0 ? (
                                        <span
                                            className="badge rounded-pill badge-success position-absolute"
                                            style={{ top: -6, right: -8, fontSize: "10px", minWidth: "16px" }}
                                        >
                                            {chatThreadsUnread > 99 ? "99+" : chatThreadsUnread}
                                        </span>
                                    ) : null}
                                </div>
                            </li>
                            <li className="onhover-dropdown pe-2">
                                <span
                                    className="cursor-pointer d-flex align-items-center"
                                    onClick={handleFullscreen}
                                    onKeyDown={(e) => e.key === "Enter" && handleFullscreen()}
                                    role="button"
                                    tabIndex={0}
                                    title="Fullscreen"
                                >
                                    <Maximize size={20} />
                                </span>
                            </li>
                            <li className="onhover-dropdown pe-2">
                                <div
                                    className={`notification-box position-relative d-inline-flex align-items-center justify-content-center ${notificationCount > 0 ? "animated" : ""}`}
                                    style={{ cursor: "pointer", minWidth: 28 }}
                                    title="Notifications"
                                >
                                    <Bell size={20} />
                                    {notificationCount > 0 && (
                                        <span
                                            className="badge rounded-pill badge-secondary position-absolute"
                                            style={{
                                                top: -4,
                                                right: -4,
                                                fontSize: "10px",
                                                minWidth: "16px",
                                                height: "16px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "0 4px",
                                            }}
                                        >
                                            {notificationCount > 99 ? "99+" : notificationCount}
                                        </span>
                                    )}
                                </div>
                            </li>
                            <li className="profile-nav onhover-dropdown pe-0 py-0">
                                <div className="media profile-media" style={{ alignItems: "center" }}>
                                    <img
                                        width="35"
                                        height="35"
                                        className="rounded-circle m-0"
                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=0D8ABC&color=fff&size=70`}
                                        alt=""
                                        style={{ objectFit: "cover" }}
                                    />
                                    <div className="media-body ms-2" style={{ textTransform: "capitalize" }}>
                                        <span className="d-block fw-semibold text-dark">{displayName}</span>
                                        <small className="text-muted">{roleLabel}</small>
                                    </div>
                                </div>
                                <ul className="simple-list profile-dropdown onhover-show-div">
                                    <li className="d-flex align-items-center p-2">
                                        <User style={{ width: 22 }} className="me-2" />
                                        <span>Account</span>
                                    </li>
                                    <li className="d-flex align-items-center p-2" onClick={handleLogout}>
                                        <LogOut style={{ width: 22 }} className="me-2" />
                                        <span>Logout</span>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

export default Header;
